/**
 * Centralized Event Manager for Integration Settings
 *
 * This utility provides a single event system for all integration settings components.
 * Instead of multiple custom events, we use one event name with different action types.
 *
 * All events are dispatched on the document and bubble up, making them accessible
 * to all components and 3rd party integrations.
 *
 * @example
 * // Dispatch an event
 * dispatchEvent('button-click', { name: 'test', value: 'value' }, rowSubmission);
 *
 * // Listen for events
 * listenForEvent('data-update', (event) => {
 *   const { data } = event.detail.data;
 *   console.log('Data updated:', data);
 * });
 */
/**
 * Dispatch an integration settings event
 *
 * Creates and dispatches a custom event with the specified action, data, and row context.
 * Events are dispatched on the document and bubble up through the DOM.
 *
 * @param action - The type of action that occurred
 * @param data - Action-specific data payload
 * @param rowSubmission - Row submission context, if applicable
 *
 * @example
 * // Dispatch a button click event
 * dispatchEvent('button-click', { name: 'save', value: 'true' }, rowSubmission);
 *
 * // Dispatch a data update event
 * dispatchEvent('data-update', { data: newData }, false);
 */
export function dispatchEvent(action, data, rowSubmission) {
    const event = new CustomEvent('integration-settings-event', {
        detail: {
            action,
            data,
            rowSubmission,
        },
        bubbles: true,
        composed: true
    });
    document.dispatchEvent(event);
}
/**
 * Listen for integration settings events of a specific action type
 *
 * Sets up an event listener that filters events by action type.
 * The handler is called only for events matching the specified action.
 *
 * @param action - The action type to listen for
 * @param handler - Function called when matching events occur
 *
 * @example
 * // Listen for data updates
 * listenForEvent('data-update', (event) => {
 *   const { data } = event.detail.data;
 *   updateUI(data);
 * });
 *
 * // Listen for button clicks
 * listenForEvent('button-click', (event) => {
 *   const { data, rowSubmission } = event.detail;
 *   handleButtonClick(data, rowSubmission);
 * });
 */
export function listenForEvent(action, handler) {
    document.addEventListener('integration-settings-event', (event) => {
        const customEvent = event;
        if (customEvent.detail.action === action) {
            handler(customEvent);
        }
    });
}
/**
 * Remove an event listener for integration settings events
 *
 * Removes a previously added event listener. Note that this function
 * must be called with the exact same handler function that was used
 * in the corresponding listenForEvent call.
 *
 * @param action - The action type the listener was listening for
 * @param handler - The handler function to remove
 */
export function removeEventListener(action, handler) {
    document.removeEventListener('integration-settings-event', (event) => {
        const customEvent = event;
        if (customEvent.detail.action === action) {
            handler(customEvent);
        }
    });
}
